import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Shield, Scale, FileText, AlertTriangle } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Terms of Service</h1>
          <p className="text-muted-foreground text-lg">
            Last Updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <div className="flex gap-2 mt-4">
            <Badge variant="secondary">UK GDPR Compliant</Badge>
            <Badge variant="secondary">DPA 2018 Aligned</Badge>
            <Badge variant="secondary">ROA 1974 Aware</Badge>
          </div>
        </div>

        <ScrollArea className="h-[70vh]">
          <div className="space-y-8 pr-4">
            {/* Introduction */}
            <Card className="section-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  1. Introduction and Agreement Acceptance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p>
                  These Terms of Service (the "Terms", "Terms of Use", or "Terms and Conditions") constitute a legally binding agreement between you ("User", "you", or "your") and Second Chance Connect ("Provider", "Platform", "we", "us", or "our").
                </p>
                
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Key Definitions</h4>
                  <ul className="space-y-2 text-xs">
                    <li><strong>Provider/Platform:</strong> Second Chance Connect, the entity offering the digital matching service.</li>
                    <li><strong>User:</strong> Any individual or entity accessing the Platform, including Job Seekers and Employers.</li>
                    <li><strong>Job Seeker:</strong> An individual seeking employment opportunities via the Platform.</li>
                    <li><strong>Employer:</strong> An entity posting employment opportunities and utilizing matching services.</li>
                    <li><strong>User Generated Content (UGC):</strong> Data, profiles, resumes, job postings, messages uploaded by Users.</li>
                    <li><strong>Criminal Offence Data:</strong> Personal data relating to criminal convictions as defined under Article 10 UK GDPR.</li>
                    <li><strong>Spent Conviction:</strong> A conviction treated as spent under the UK Rehabilitation of Offenders Act 1974.</li>
                  </ul>
                </div>

                <p className="font-medium">
                  By accessing or using the Platform, you affirm your explicit acceptance and agreement to be legally bound by these Terms. If you do not agree, you must not use the Platform.
                </p>
              </CardContent>
            </Card>

            {/* User Accounts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  2. User Accounts and Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Eligibility and Account Management</h4>
                  <p>
                    Users must provide accurate, current, and complete information during registration. You are responsible for all activities under your account and must maintain credential confidentiality. Notify us immediately of any unauthorized access.
                  </p>
                </div>

                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    Job Seeker Obligations
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>You warrant that all UGC (resumes, profiles) is truthful, accurate, and complete.</li>
                    <li>You are solely responsible for job search decisions and applications.</li>
                    <li>The Platform makes NO guarantee regarding job quality, safety, wages, or any employment aspect.</li>
                    <li>You must research employer integrity and verify job details independently.</li>
                    <li>Exercise due diligence, common sense, and caution when engaging with Employers.</li>
                  </ul>
                </div>

                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    Employer Legal Compliance
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>You are solely responsible for all employment decisions, including hiring, screening, and interviewing.</li>
                    <li>You must ensure strict compliance with UK Equality Act 2010 and all employment laws.</li>
                    <li>You are solely responsible for conducting background checks and vetting procedures.</li>
                    <li>Collection of Criminal Offence Data must comply with UK GDPR Article 10 and DPA 2018.</li>
                    <li>You must understand statutory limitations under the Rehabilitation of Offenders Act 1974.</li>
                    <li>The Platform does NOT provide legal, HR, or professional advice.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Acceptable Use */}
            <Card>
              <CardHeader>
                <CardTitle>3. Acceptable Use Policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p className="font-medium">Users are strictly prohibited from:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Creating false, misleading, or fraudulent UGC</li>
                  <li>Uploading malicious content, viruses, or harmful code</li>
                  <li>Engaging in spam, harassment, or illegal activities</li>
                  <li>Violating intellectual property rights</li>
                  <li>Scraping, automating data extraction, or reverse engineering the Platform</li>
                  <li>Sharing account credentials or circumventing access controls</li>
                  <li>Impersonating individuals or organisations</li>
                </ul>
                <p className="text-xs text-muted-foreground">
                  We reserve the right to access, review, modify, or remove User Content without notice if it violates these Terms.
                </p>
              </CardContent>
            </Card>

            {/* Data Privacy - CRITICAL SECTION */}
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Scale className="h-5 w-5" />
                  4. Data Privacy and Criminal Offence Data (UK GDPR Article 10)
                </CardTitle>
                <CardDescription>
                  This Platform processes highly sensitive personal data subject to strict UK legal requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/30">
                  <h4 className="font-semibold mb-2 text-destructive">Processing Criminal Convictions Data</h4>
                  <p className="text-xs mb-3">
                    Processing Criminal Offence Data requires TWO distinct legal requirements under UK GDPR:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-xs">
                    <li>
                      <strong>Lawful Basis (Article 6 UK GDPR):</strong> Contractual necessity or legitimate interest
                    </li>
                    <li>
                      <strong>Specific Condition (Article 10 / DPA 2018 Schedule 1):</strong> Explicit Consent or necessity under UK employment law
                    </li>
                  </ol>
                </div>

                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Explicit Consent Requirements</h4>
                  <p className="text-xs mb-2">
                    The Platform enforces "explicit consent" for Criminal Offence Data processing, which must be:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li><strong>Freely Given and Specific:</strong> Clearly specifying exact categories of criminal data</li>
                    <li><strong>Unambiguous and Affirmative:</strong> Confirmed via clear opt-in mechanism</li>
                    <li><strong>Separate Disclosure:</strong> Distinctly separated from general Terms consent</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Rehabilitation of Offenders Act 1974 (ROA) Compliance</h4>
                  <p className="text-xs mb-2">
                    The Platform acknowledges the ROA 1974's purpose to support rehabilitation of reformed offenders.
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                    <li>Job Seekers are NOT legally required to disclose spent convictions unless the role is ROA-exempt</li>
                    <li>Job Seekers must understand their rights when deciding which information to include</li>
                    <li>Employers are solely responsible for understanding ROA exemptions when requesting criminal history data</li>
                    <li>The Provider disclaims liability for unlawful disclosure or unlawful requests outside legal exemptions</li>
                  </ul>
                </div>

                <p className="text-xs text-muted-foreground">
                  Full details on data processing, retention, security, and User rights are in our Privacy Policy, incorporated by reference.
                </p>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card>
              <CardHeader>
                <CardTitle>5. Intellectual Property and Content Licensing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Platform Intellectual Property</h4>
                  <p className="text-xs">
                    All Platform Content (design, logos, software, algorithms) is protected by copyright, trademark, and other laws. 
                    Users are prohibited from copying, reproducing, scraping, or reverse engineering without written authorization.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">User Generated Content Ownership</h4>
                  <p className="text-xs mb-2">
                    You retain ownership of your UGC. By using the Platform, you grant us a non-exclusive, worldwide, royalty-free, 
                    transferable license to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Host, reproduce, modify, process, display, and distribute your UGC</li>
                    <li><strong>Process and analyze UGC for developing, training, and improving AI/ML models and algorithms</strong></li>
                    <li>Use aggregated, anonymized, or de-identified data to enhance Platform services</li>
                  </ul>
                </div>

                <p className="text-xs text-muted-foreground">
                  This license survives account termination and is essential for Platform operation and technological development.
                </p>
              </CardContent>
            </Card>

            {/* Disclaimers - CRITICAL */}
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">6. Warranties, Disclaimers, and Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="bg-destructive/10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">"AS IS" Provision</h4>
                  <p className="text-xs">
                    THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE". WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, 
                    INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                  </p>
                </div>

                <div className="bg-destructive/10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">No Guarantee of Vetting or Legitimacy</h4>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>We make NO guarantee about the legitimacy, integrity, or background of any User</li>
                    <li>We do NOT guarantee job availability, accuracy, or quality</li>
                    <li>We disclaim responsibility for safety, wages, working conditions, or employment outcomes</li>
                    <li>Each User must conduct their own due diligence and background verification</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Limitation of Liability</h4>
                  <p className="text-xs mb-2">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                    CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES.
                  </p>
                  <p className="text-xs">
                    Our total aggregate liability is limited to the greater of (a) amounts paid by you in the 12 months 
                    preceding the claim, or (b) £100.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Indemnification</h4>
                  <p className="text-xs">
                    You agree to indemnify, defend, and hold harmless the Provider from all third-party claims arising from:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Your breach of these Terms</li>
                    <li>Your violation of employment, anti-discrimination, or data protection laws</li>
                    <li>Any User-to-User dispute or employment-related claim</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">
                    This indemnification obligation survives for five (5) years after account termination.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card>
              <CardHeader>
                <CardTitle>7. Governing Law, Jurisdiction, and Dispute Resolution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Governing Law</h4>
                  <p className="text-xs">
                    These Terms shall be governed by and construed in accordance with the laws of England and Wales.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Dispute Resolution Process</h4>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li><strong>Good Faith Negotiation:</strong> Parties shall first attempt direct negotiation</li>
                    <li><strong>Mediation (Optional):</strong> Non-binding mediation via recognized ADR provider</li>
                    <li><strong>Litigation:</strong> Disputes proceed to the exclusive jurisdiction of England and Wales courts</li>
                  </ol>
                </div>

                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Class Action Waiver</h4>
                  <p className="text-xs">
                    All claims must proceed individually. You waive all rights to participate in class action, 
                    collective action, or other proceedings on behalf of others.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* General Provisions */}
            <Card>
              <CardHeader>
                <CardTitle>8. General Provisions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Termination and Survival</h4>
                  <p className="text-xs mb-2">
                    Either party may terminate this agreement. Upon termination, the following provisions survive:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>IP license grants (indefinitely)</li>
                    <li>Limitation of liability clauses</li>
                    <li>Disclaimer of warranties</li>
                    <li>Indemnification obligations (5 years)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Changes to Terms</h4>
                  <p className="text-xs">
                    We reserve the right to modify these Terms. Continued use after changes constitutes acceptance. 
                    If you disagree, cease using the Platform.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Severability</h4>
                  <p className="text-xs">
                    If any provision is deemed unlawful or unenforceable, it shall be severable without affecting 
                    the validity of remaining provisions.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Entire Agreement</h4>
                  <p className="text-xs">
                    These Terms, together with the Privacy Policy and Acceptable Use Policy, constitute the entire 
                    agreement and supersede all prior agreements.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-primary/5">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="mb-2">
                  For questions about these Terms, please contact us at:
                </p>
                <p className="text-xs text-muted-foreground">
                  Email: legal@secondchanceconnect.uk
                </p>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
